import { QueryKey } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CachedMovieData } from '@cineamo/api/models/movie/CachedMovieData.types';

import { CountryCode } from '@cineamo/api/types/CountryCode';

import { TMDB_IMAGE_RESOLUTION } from '@cineamo/api/constants/tmdb-constants';

import {
    getMovieAgeRating,
    getMovieBackdropBlurDataImage,
    getMovieBackdropImage,
    getMovieLogoImage,
    getMovieOverview,
    getMoviePosterBlurDataImage,
    getMoviePosterImage,
    getMovieProductionCountries,
    getMovieReleaseDate,
    getMovieReleaseYear,
    getMovieRuntime,
    getMovieTitle,
    getMovieTrailer,
    MovieVersionDto
} from '@cineamo/api/helpers/movie-helper';

import { getLanguageCodeFromString, getCountryCodeFromString } from '@cineamo/ui/src/helpers/locale-helper';
import { useAgeRatingText } from '@cineamo/ui/src/helpers/movie-helper';

import useLocale from '@cineamo/ui/src/hooks/useLocale';

type CachedMovieDataReturn = {
    data: CachedMovieData | undefined;
};

/**
 * Returns derived/cached movie data via useMemo. This used to be wrapped in a
 * useQuery without staleTime, which spawned a per-row cache entry that was
 * always immediately stale and recomputed on every render. The function does
 * not fetch anything — useMemo gives the same memoization benefit without the
 * cache-thrash.
 */
export default function useCachedMovieData<TKey extends QueryKey = QueryKey>(
    options: {
        movie?: MovieVersionDto;
        countryCode?: CountryCode;
        locale: string;
    },
    deps?: TKey
): CachedMovieDataReturn {
    const { locale: browserLocale, countryCode } = useLocale();

    const effectiveCountryCode = options.countryCode || countryCode;
    const ageRating = getMovieAgeRating(options.movie, effectiveCountryCode);
    const ageRatingText = useAgeRatingText(ageRating, effectiveCountryCode);

    const data = useMemo(() => {
        if (!options.movie) return undefined;
        const cached = getCachedMovieData({
            movie: options.movie,
            locale: options.locale || browserLocale || '',
            countryCode: effectiveCountryCode
        });
        cached.ageRatingText = ageRatingText;
        return cached;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.movie?.tmdbId, options.locale, effectiveCountryCode, browserLocale, ageRatingText, deps]);

    return { data };
}

/**
 * `movie` is required: everything here is derived from it, so "no movie" has no useful answer —
 * every caller guards and skips this instead of asking for a hollow object.
 */
export function getCachedMovieData(options: {
    movie: MovieVersionDto;
    countryCode?: CountryCode;
    locale: string;
}): CachedMovieData {
    const ageRating = getMovieAgeRating(options.movie, options.countryCode);
    const language = getLanguageCodeFromString(options.locale);
    const countryCode = options.countryCode || getCountryCodeFromString(options.locale);
    // Selected here, flattened into the four fields below — the DTO itself does not travel.
    const trailer = getMovieTrailer(options.movie, { language, countryCode });

    return {
        id: options.movie?.id,
        tmdbId: options.movie?.tmdbId,
        title: getMovieTitle(options.movie, options.locale, options.countryCode),
        releaseDate: getMovieReleaseDate(options.movie, options.countryCode),
        releaseYear: getMovieReleaseYear(options.movie, options.countryCode),
        ageRating: ageRating,
        overview: getMovieOverview(options.movie, options.locale),
        genres: options.movie?.genres || [],
        originalLanguage: options.movie?.originalLanguage,
        productionCountries: getMovieProductionCountries(options.movie, options.locale),
        runtime: getMovieRuntime(options.movie, options.locale),
        trailerUrl: trailer?.fullUrl,
        trailerName: trailer?.name,
        trailerThumbnailUrl: trailer?.thumbnailUrl,
        trailerPublishedAt: trailer?.publishedAt,
        // Movie logo (title treatment) in the viewer's language only — same picking logic as the web
        // hero; no fallback, so a missing logo falls back to the title text downstream.
        logoImageURL_W780: getMovieLogoImage(options.movie, {
            language,
            countryCode,
            usePlaceholder: false,
            imageOptions: {
                resolution: TMDB_IMAGE_RESOLUTION.W780
            }
        }),
        backdropImageURL_ORIGINAL: getMovieBackdropImage(options.movie, {
            language,
            countryCode,
            usePosterAsFallback: true,
            usePlaceholder: false,
            imageOptions: {
                resolution: TMDB_IMAGE_RESOLUTION.ORIGINAL
            }
        }),
        // Named resolutions, not a map of every one TMDB offers: a surface that needs another adds
        // the one field it needs — see the note on `CachedMovieData`.
        backdropImageURL_W1280: getMovieBackdropImage(options.movie, {
            language,
            countryCode,
            usePosterAsFallback: true,
            usePlaceholder: false,
            imageOptions: {
                resolution: TMDB_IMAGE_RESOLUTION.W1280
            }
        }),
        backdropImageURL_W780: getMovieBackdropImage(options.movie, {
            language,
            countryCode,
            usePosterAsFallback: true,
            usePlaceholder: false,
            imageOptions: {
                resolution: TMDB_IMAGE_RESOLUTION.W780
            }
        }),
        backdropBlurDataImage: getMovieBackdropBlurDataImage(options.movie, {
            language,
            countryCode,
            usePosterAsFallback: false,
            usePlaceholder: false
        }),
        posterImageURL_W780: getMoviePosterImage(options.movie, {
            language,
            countryCode,
            useBackdropAsFallback: true,
            usePlaceholder: false,
            imageOptions: {
                resolution: TMDB_IMAGE_RESOLUTION.W780
            }
        }),
        posterBlurDataImage: getMoviePosterBlurDataImage(options.movie, {
            language,
            countryCode,
            useBackdropAsFallback: true,
            usePlaceholder: false
        })
    };
}
